import React, { useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ChevronRight, Plus } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ContactSection from '../components/ContactSection';
import { useSiteContent } from '../context/SiteContentContext';
import { resolveImageUrl } from '../utils/urls';
import SEO from '../components/SEO';

const PAGE_BANNER = 'https://jilongot.com/public/upload/20240703/240F3163430613.jpg';

const Breadcrumb = ({ items }) => (
  <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5 flex flex-wrap items-center gap-2 text-sm text-gray-600" data-testid="product-breadcrumb">
    {items.map((it, i) => (
      <React.Fragment key={i}>
        {i > 0 && <ChevronRight size={14} className="text-gray-400" />}
        {it.to ? (
          <Link to={it.to} className="hover:text-[#0a3d8f] transition-colors">{it.label}</Link>
        ) : (
          <span className="text-[#0a3d8f] font-semibold">{it.label}</span>
        )}
      </React.Fragment>
    ))}
  </div>
);

const PageHero = ({ title, subtitle }) => (
  <section className="relative h-[280px] md:h-[340px] overflow-hidden" data-testid="product-hero">
    <img src={PAGE_BANNER} alt="Products" className="absolute inset-0 w-full h-full object-cover" />
    <div className="absolute inset-0 bg-gradient-to-r from-[#0a3d8f]/85 via-[#0a3d8f]/60 to-transparent" />
    <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-full flex flex-col justify-center">
      <p className="text-white/80 text-sm uppercase tracking-[0.3em] mb-3">Products</p>
      <h1 className="text-white text-3xl md:text-5xl font-extrabold mb-3 font-[Montserrat]">{title}</h1>
      {subtitle && <p className="text-white/90 text-base md:text-lg max-w-2xl leading-relaxed">{subtitle}</p>}
    </div>
  </section>
);

const IntroBlock = ({ title, description }) => (
  <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-4 pb-10">
    <h2 className="text-2xl md:text-3xl font-extrabold text-gray-900 mb-3">{title}</h2>
    <div className="w-16 h-1 bg-[#0a3d8f] mb-5 rounded-full" />
    {description && <p className="text-gray-600 leading-relaxed max-w-4xl">{description}</p>}
  </div>
);

const SectionLabel = ({ children }) => (
  <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-6">
    <div className="flex items-center gap-3">
      <span className="inline-block w-2 h-6 bg-[#0a3d8f] rounded-sm"></span>
      <h3 className="text-xl font-bold text-gray-900 uppercase tracking-wide">{children}</h3>
    </div>
  </div>
);

// Card mirroring jilongot.com/en/product hover-overlay pattern
const CategoryCard = ({ image, title, description, to, testId }) => (
  <Link
    to={to}
    data-testid={testId}
    className="group relative bg-white rounded-lg overflow-hidden border border-gray-200 hover:border-[#0a3d8f] shadow-sm hover:shadow-xl transition-all duration-300"
  >
    <div className="relative h-56 md:h-64 overflow-hidden bg-gray-50">
      <img src={resolveImageUrl(image)} alt={title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" />
      <div className="absolute top-3 right-3 bg-white/95 backdrop-blur rounded-full px-3 py-1.5 flex items-center gap-1 text-xs font-semibold text-[#0a3d8f] opacity-0 group-hover:opacity-100 transition-opacity">
        Details <Plus size={12} strokeWidth={3} />
      </div>
    </div>
    <div className="p-5 border-t border-gray-100">
      <h4 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-[#0a3d8f] transition-colors">{title}</h4>
      <p className="text-sm text-gray-600 leading-relaxed line-clamp-3">{description}</p>
    </div>
  </Link>
);

const ProductCard = ({ product }) => (
  <Link
    to={`/produk/detail/${product.id}`}
    data-testid={`product-item-${product.id}`}
    className="group relative bg-white rounded-lg overflow-hidden border border-gray-200 hover:border-[#0a3d8f] shadow-sm hover:shadow-xl transition-all duration-300 block"
  >
    <div className="relative h-52 overflow-hidden bg-gradient-to-br from-gray-50 to-white p-6 flex items-center justify-center">
      <img src={resolveImageUrl(product.image)} alt={product.name} className="max-h-full max-w-full object-contain transition-transform duration-500 group-hover:scale-105" />
      <div className="absolute top-3 right-3 bg-white/95 backdrop-blur rounded-full px-3 py-1.5 flex items-center gap-1 text-xs font-semibold text-[#0a3d8f] opacity-0 group-hover:opacity-100 transition-opacity">
        Details <Plus size={12} strokeWidth={3} />
      </div>
      {product.model && (
        <div className="absolute top-3 left-3 bg-[#0a3d8f] text-white text-[10px] font-bold px-2 py-1 rounded-sm tracking-wider">
          {product.model}
        </div>
      )}
    </div>
    <div className="p-5 border-t border-gray-100">
      <h4 className="text-base font-bold text-gray-900 mb-2 group-hover:text-[#0a3d8f] transition-colors line-clamp-2 min-h-[3rem]">
        {product.name}
      </h4>
      <ul className="space-y-1.5">
        {(product.features || []).slice(0, 3).map((f, i) => (
          <li key={i} className="flex items-start gap-2 text-xs text-gray-600 leading-relaxed">
            <span className="text-[#0a3d8f] font-bold mt-0.5">•</span>
            <span className="flex-1">{f}</span>
          </li>
        ))}
      </ul>
    </div>
  </Link>
);

const ProductPage = () => {
  const { categorySlug, subcategorySlug } = useParams();
  const { productCategories, productSubcategories, products } = useSiteContent();

  const currentCategory = useMemo(
    () => productCategories.find((c) => c.id === categorySlug),
    [productCategories, categorySlug]
  );
  const currentSub = useMemo(
    () => productSubcategories.find((s) => s.id === subcategorySlug),
    [productSubcategories, subcategorySlug]
  );

  // ---- Level 1: /produk (all top-level categories) ----
  if (!categorySlug) {
    return (
      <>
        <SEO path="/produk" pageName="Produk" />
        <Header />
        <main data-testid="product-page-root">
          <PageHero
            title="Products"
            subtitle="Klasifikasi akurat, pilih sesuai kebutuhan Anda"
          />
          <Breadcrumb items={[{ label: 'Home', to: '/' }, { label: 'Products' }]} />
          <IntroBlock
            title="Peralatan & alat serat optik standar bagi para profesional komunikasi"
            description="JILONG, penyedia global produk, teknologi, dan solusi komunikasi optik, menyediakan solusi untuk fiber-optic fusion, pengukuran serat optik, transmisi ODN, dan fiber optic cable. Peralatan dan alat serat optik canggih membantu Anda melakukan fusion, troubleshooting, dan pemeliharaan dengan cepat, sementara OTDR dan instrumen pengukur menjaga integritas jaringan serat optik yang sensitif tanpa gangguan."
          />
          <SectionLabel>Product Category</SectionLabel>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {productCategories
                .filter((cat) => !['odn', 'fiber-cables'].includes(cat.id))
                .map((cat) => (
                <CategoryCard
                  key={cat.id}
                  image={cat.image}
                  title={cat.name}
                  description={cat.description}
                  to={`/produk/${cat.id}`}
                  testId={`category-card-${cat.id}`}
                />
              ))}
            </div>
          </div>
          <ContactSection />
        </main>
        <Footer />
      </>
    );
  }

  // ---- Level 2: /produk/:categorySlug (subcategories) ----
  if (categorySlug && !subcategorySlug) {
    const subs = productSubcategories.filter((s) => s.parent === categorySlug);
    const catTitle = currentCategory?.name || categorySlug;
    const catDesc = currentCategory?.description || '';
    return (
      <>
        <SEO path={`/produk/${categorySlug}`} pageName={catTitle} description={catDesc} />
        <Header />
        <main data-testid="product-page-category">
          <PageHero title={catTitle} subtitle={catDesc} />
          <Breadcrumb items={[
            { label: 'Home', to: '/' },
            { label: 'Products', to: '/produk' },
            { label: catTitle },
          ]} />
          <IntroBlock
            title={`${catTitle} — Solusi Lengkap`}
            description={catDesc}
          />
          {subs.length > 0 ? (
            <>
              <SectionLabel>{catTitle} Category</SectionLabel>
              <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {subs.map((sub) => (
                    <CategoryCard
                      key={sub.id}
                      image={sub.image}
                      title={sub.name}
                      description={sub.description}
                      to={`/produk/${categorySlug}/${sub.id}`}
                      testId={`subcategory-card-${sub.id}`}
                    />
                  ))}
                </div>
              </div>
            </>
          ) : (
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
              <div className="bg-blue-50 border border-blue-100 rounded-lg p-10 text-center">
                <p className="text-gray-700 text-lg">Kategori produk ini akan segera hadir.</p>
                <p className="text-sm text-gray-500 mt-2">Hubungi tim kami untuk info lebih lanjut.</p>
                <Link to="/produk" className="inline-block mt-5 px-6 py-2.5 bg-[#0a3d8f] text-white rounded-md text-sm font-semibold hover:bg-[#082e6b] transition-colors">
                  ← Kembali ke Products
                </Link>
              </div>
            </div>
          )}
          <ContactSection />
        </main>
        <Footer />
      </>
    );
  }

  // ---- Level 3: /produk/:categorySlug/:subcategorySlug (product list) ----
  const filtered = products.filter((p) => p.subcategory === subcategorySlug);
  const subTitle = currentSub?.name || subcategorySlug;
  const subDesc = currentSub?.description || '';
  const catTitle = currentCategory?.name || categorySlug;

  return (
    <>
      <SEO path={`/produk/${categorySlug}/${subcategorySlug}`} pageName={subTitle} description={subDesc} />
      <Header />
      <main data-testid="product-page-subcategory">
        <PageHero title={subTitle} subtitle={subDesc} />
        <Breadcrumb items={[
          { label: 'Home', to: '/' },
          { label: 'Products', to: '/produk' },
          { label: catTitle, to: `/produk/${categorySlug}` },
          { label: subTitle },
        ]} />
        <IntroBlock title={subTitle} description={subDesc} />
        {filtered.length > 0 ? (
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {filtered.map((p) => (
                <ProductCard key={p.id} product={p} />
              ))}
            </div>
          </div>
        ) : (
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
            <div className="bg-blue-50 border border-blue-100 rounded-lg p-10 text-center">
              <p className="text-gray-700 text-lg">Produk untuk kategori ini akan segera hadir.</p>
              <Link to={`/produk/${categorySlug}`} className="inline-block mt-5 px-6 py-2.5 bg-[#0a3d8f] text-white rounded-md text-sm font-semibold hover:bg-[#082e6b] transition-colors">
                ← Kembali ke {catTitle}
              </Link>
            </div>
          </div>
        )}
        <ContactSection />
      </main>
      <Footer />
    </>
  );
};

export default ProductPage;
